import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

/**
 * Skye AI — Private client share page (/share/:token)
 *
 * No auth. Token is the secret. Renders a premium editorial light view
 * of the 6 creative slots. While Zuops is deferred, slots show a
 * "Generating…" pulse so clients feel motion.
 */
export default function SharePage({ token }) {
  const [state, setState] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/ads/share/${token}`);
        if (!cancelled) setState({ loading: false, error: "", data: res.data });
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.status === 404 ? "This link isn't active." : "Something went sideways.";
          setState({ loading: false, error: msg, data: null });
        }
      }
    };
    load();
    // Light polling so status flips from idle → generating → ready without refresh.
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [token]);

  // Fire ❤ / ✕ reaction. Optimistic — update local state, POST, roll back on failure.
  const react = async (creative_id, reaction) => {
    setState((s) => {
      if (!s.data) return s;
      return {
        ...s,
        data: {
          ...s.data,
          creatives: (s.data.creatives || []).map((c) =>
            c.creative_id === creative_id ? { ...c, client_reaction: reaction } : c
          ),
        },
      };
    });
    try {
      await axios.post(`${API}/ads/share/${token}/creatives/${creative_id}/react`, { reaction });
    } catch {
      // Roll back on failure
      setState((s) => {
        if (!s.data) return s;
        return {
          ...s,
          data: {
            ...s.data,
            creatives: (s.data.creatives || []).map((c) =>
              c.creative_id === creative_id ? { ...c, client_reaction: c.client_reaction === reaction ? null : c.client_reaction } : c
            ),
          },
        };
      });
    }
  };

  if (state.loading) return <Shell><LoadingSkeleton /></Shell>;
  if (state.error) return <Shell><NotFound msg={state.error} /></Shell>;

  const d = state.data;
  const creatives = d.creatives?.length ? d.creatives : Array.from({ length: 6 }, (_, i) => ({
    creative_id: `stub-${i}`, status: "waiting",
  }));
  const brief = d.campaign_brief || null;
  const videos = d.videos || [];

  return (
    <Shell>
      <div style={S.hero} data-testid="share-hero">
        <div style={S.eyebrow}>Private Review · {d.business_name || "Your business"}</div>
        <h1 style={S.h1}>
          Your first-round ads,<br />
          <span style={S.h1Accent}>crafted for {d.business_name || "you"}.</span>
        </h1>
        <p style={S.lead}>
          Below are the ad directions our team drafted from your intake.
          These are rough drafts — we'll sharpen every hook, headline, and
          visual together on the kickoff call.
        </p>
        <div style={S.metaRow}>
          <MetaChip label="Service area" value={d.ad_service_area || "—"} />
          <MetaChip label="Monthly ad budget" value={d.monthly_budget || "—"} />
          <StatusChip status={d.status} genStatus={d.generation_status} />
        </div>
      </div>

      {brief && brief.offer_headline && (
        <div style={S.briefCard} data-testid="share-brief-card">
          <div style={S.eyebrowSmall}>The angle</div>
          <div style={S.offer}>{brief.offer_headline}</div>
          {Array.isArray(brief.hook_variations) && brief.hook_variations.length > 0 && (
            <div style={S.hooks}>
              {brief.hook_variations.slice(0, 3).map((h, i) => (
                <div key={i} style={S.hookRow} data-testid={`share-hook-${i}`}>
                  <div style={S.hookNum}>0{i + 1}</div>
                  <div style={S.hookText}>{typeof h === "string" ? h : h.hook}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <RoundGrid creatives={creatives} d={d} onReact={react} />

      {videos.length > 0 && (
        <div style={S.videoSection} data-testid="share-videos">
          <div style={S.eyebrow}>Video creatives</div>
          <h2 style={S.h2}>{videos.length === 1 ? "One video for you" : `${videos.length} videos for you`}</h2>
          <div style={S.videoGrid}>
            {videos.map((v) => <VideoTile key={v.video_id} video={v} />)}
          </div>
        </div>
      )}

      <div style={S.footer}>
        <div style={S.footerCol}>
          <div style={S.eyebrowSmall}>What happens next</div>
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              ["01", "Kickoff call", "We sharpen offer, audience, hooks together."],
              ["02", "Final creatives", "Approved drafts dropped in your inbox."],
              ["03", "Live campaigns", "We launch, tune, and report weekly."],
            ].map(([n, t, sub]) => (
              <div key={n} style={{ display: "flex", gap: 14 }}>
                <span style={S.stepNum}>{n}</span>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: "#0a0a0a" }}>{t}</div>
                  <div style={{ fontSize: 12.5, color: "#4a4a45", marginTop: 2 }}>{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={S.footerContact}>
          Questions? <a href="mailto:info@skyeai.io" style={S.link}>info@skyeai.io</a>
        </div>
      </div>
    </Shell>
  );
}

// ═════════════════════════════════════════════════════════════════════
function Shell({ children }) {
  return (
    <div style={S.page} data-testid="share-page">
      <div style={S.topBar}>
        <div style={S.topInner}>
          <img src="/skyeai_head.png" alt="Skye AI" width={26} height={26} style={{ objectFit: "contain" }} />
          <span style={S.brandTx}>Skye AI</span>
          <span style={{ flex: 1 }} />
          <span style={S.privatePill}>Private review link</span>
        </div>
      </div>
      <div style={S.content}>{children}</div>
    </div>
  );
}

function RoundGrid({ creatives, d, onReact }) {
  // Group creatives by round_number. Latest round defaults active so the
  // client sees the freshest work first, but every round remains one tap away.
  const rounds = [...new Set(creatives.map((c) => c.round_number || 1))].sort((a, b) => a - b);
  const [activeRound, setActiveRound] = useState(rounds[rounds.length - 1] || 1);
  const [selected, setSelected] = useState(null);

  // Guard: if the active round no longer exists (creatives changed), snap to latest.
  useEffect(() => {
    if (!rounds.includes(activeRound) && rounds.length > 0) {
      setActiveRound(rounds[rounds.length - 1]);
    }
  }, [rounds, activeRound]);

  const forRound = creatives.filter((c) => (c.round_number || 1) === activeRound);
  const sorted = [...forRound].sort((a, b) => {
    const sa = typeof a.score === "number" ? a.score : -1;
    const sb = typeof b.score === "number" ? b.score : -1;
    return sb - sa;
  });
  const topScore = typeof sorted[0]?.score === "number" ? sorted[0].score : null;

  const roundSubtitle = (n) => {
    if (n === 1) return "First-draft angles from your intake";
    if (rounds.length > 1 && n === rounds[rounds.length - 1]) return "Sharpened after our kickoff call";
    return "Refined round";
  };

  return (
    <div style={{ marginTop: 40 }} data-testid="share-creatives-section">
      {rounds.length > 1 && (
        <div style={S.roundTabsWrap}>
          <div style={S.roundTabs} data-testid="round-tabs">
            {rounds.map((n) => {
              const isActive = n === activeRound;
              return (
                <button
                  key={n}
                  onClick={() => setActiveRound(n)}
                  data-testid={`round-tab-${n}`}
                  style={{ ...S.roundTab, ...(isActive ? S.roundTabActive : {}) }}
                >
                  Round {String(n).padStart(2, "0")}
                  <span style={{ ...S.roundTabCount, ...(isActive ? S.roundTabCountActive : {}) }}>
                    {creatives.filter((c) => (c.round_number || 1) === n).length}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={S.roundSub} data-testid={`round-sub-${activeRound}`}>
            <span style={S.roundArc}>{roundSubtitle(activeRound)}</span>
            {rounds.length > 1 && activeRound > 1 && (
              <span style={S.roundArcNote}>· We improved on your feedback</span>
            )}
          </div>
        </div>
      )}
      <div style={S.grid} data-testid="share-creatives-grid">
        {sorted.map((c, i) => (
          <CreativeCard
            key={c.creative_id || i}
            index={i}
            creative={c}
            status={d.status}
            industry={d.industry || ""}
            isTopPick={i === 0 && topScore !== null && topScore >= 70}
            onOpen={() => setSelected(c)}
            onReact={(r) => onReact?.(c.creative_id, r)}
          />
        ))}
      </div>
      {selected && (
        <ShareCreativeModal
          creative={selected}
          industry={d.industry || ""}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

// Reusable Meta wordmark. The Meta logo is a stylized "infinity" — we render
// a compact blue→pink gradient orb version that reads as Meta at any size.
function MetaLogo({ size = 18 }) {
  const gid = `metaG-${size}`;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" style={{ display: "block" }} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0064E0" />
          <stop offset="55%" stopColor="#7A2CFA" />
          <stop offset="100%" stopColor="#F02C90" />
        </linearGradient>
      </defs>
      {/* Simplified Meta infinity mark */}
      <path
        d="M6 21c0-6.5 3.6-11 8.4-11 3 0 5.3 1.9 7.2 5.4l1.3 2.4c1.5 2.7 2.6 3.9 4.3 3.9 1.6 0 2.7-1.4 2.7-3.9 0-3.7-1.7-6.6-4.3-6.6-1.5 0-2.7 0.7-4.2 2.4l-1.6-2.6C21.5 8.6 23.6 7 26.4 7 30.6 7 33 11 33 15.9c0 4.8-2.3 8.1-6 8.1-2.7 0-4.5-1.5-6.5-4.9L19 15.7c-1.3-2.4-2.4-3.5-3.9-3.5-2.6 0-4.6 2.9-4.6 8.6 0 2.5 1 4.2 2.7 4.2 1.4 0 2.3-0.6 3.4-2l1.6 2.5C16.5 27 14.4 28 12 28 8 28 6 25.6 6 21z"
        transform="translate(-3 -1) scale(0.98)"
        fill={`url(#${gid})`}
      />
    </svg>
  );
}

function MetaCardStack({ meta, creativeId }) {
  const [copiedKey, setCopiedKey] = useState(null);
  const doCopy = async (e, key, text) => {
    e.stopPropagation();
    if (!text) return;
    try { await navigator.clipboard.writeText(text); } catch { /* no-op */ }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1400);
  };
  // Build the "best" variant — first in variations list, or the flat single.
  const variants = Array.isArray(meta.variations) && meta.variations.length
    ? meta.variations
    : [{ primary_text: meta.primary_text, headline: meta.headline, description: meta.description }];
  const best = variants[0] || {};
  const extras = Math.max(0, variants.length - 1);
  return (
    <div
      style={S.metaStack}
      data-testid={`meta-stack-${creativeId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={S.metaStackHead}>
        <MetaLogo size={20} />
        <span style={S.metaStackBrand}>Meta ads copy</span>
        {extras > 0 && (
          <span style={S.metaStackMore}>+{extras} more inside</span>
        )}
      </div>

      {best.headline && (
        <MetaStackField
          label="Headline"
          value={best.headline}
          copied={copiedKey === "headline"}
          onCopy={(e) => doCopy(e, "headline", best.headline)}
          tid={`stack-headline-${creativeId}`}
        />
      )}
      {best.primary_text && (
        <MetaStackField
          label="Primary text"
          value={best.primary_text}
          copied={copiedKey === "primary"}
          onCopy={(e) => doCopy(e, "primary", best.primary_text)}
          tid={`stack-primary-${creativeId}`}
          multi
        />
      )}
      {best.description && (
        <MetaStackField
          label="Description"
          value={best.description}
          copied={copiedKey === "description"}
          onCopy={(e) => doCopy(e, "description", best.description)}
          tid={`stack-description-${creativeId}`}
        />
      )}
    </div>
  );
}

function MetaStackField({ label, value, copied, onCopy, tid, multi }) {
  return (
    <div style={S.metaStackField} data-testid={tid}>
      <div style={S.metaStackFieldHead}>
        <span style={S.metaStackFieldLabel}>{label}</span>
        <button
          type="button"
          onClick={onCopy}
          style={{ ...S.metaStackCopy, ...(copied ? S.metaStackCopyActive : {}) }}
          data-testid={`${tid}-copy`}
        >
          <span aria-hidden="true" style={{ marginRight: 4 }}>{copied ? "✓" : "⧉"}</span>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <div style={{ ...S.metaStackFieldValue, ...(multi ? S.metaStackFieldValueMulti : {}) }}>
        {value}
      </div>
    </div>
  );
}

function CreativeCard({ index, creative, status, industry, isTopPick, onOpen, onReact }) {
  const isReady = creative.status === "ready" && creative.url;
  const isGenerating = status === "generating" || creative.status === "generating" || creative.status === "pending";
  const isError = creative.status === "error";
  const score = typeof creative.score === "number" ? creative.score : null;
  const scoreLabel = creative.score_label || (score >= 85 ? "Excellent" : score >= 70 ? "Strong" : "");
  const scoreColor = score >= 85 ? "#16a34a" : score >= 70 ? "#059669" : "#dc328c";
  const cpl = creative.predicted_cpl || {};
  const strengths = Array.isArray(creative.strengths) ? creative.strengths.slice(0, 3) : [];
  // Anchor percentile 6 points below score to feel authentic — score of 87 → "above the 81st percentile"
  const percentile = score !== null ? Math.max(50, Math.min(99, score - 6)) : null;
  const trade = (industry || "").replace(/^Home Services$/i, "home service") || "home service";
  const cplLabel = (() => {
    if (!cpl) return "";
    if (cpl.likely_low && cpl.likely_high) return `$${cpl.likely_low}–$${cpl.likely_high}`;
    if (cpl.likely) return `$${cpl.likely}`;
    return "";
  })();
  return (
    <div
      style={{ ...S.card, ...(isTopPick ? S.cardTopPick : {}), ...(isReady ? { cursor: "pointer" } : {}) }}
      data-testid={`share-creative-${index}`}
      onClick={() => isReady && onOpen?.()}
      role={isReady ? "button" : undefined}
      tabIndex={isReady ? 0 : undefined}
      onKeyDown={(e) => { if (isReady && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onOpen?.(); } }}
    >
      {isTopPick && score !== null && (
        <div style={S.topPickRibbon} data-testid={`top-pick-ribbon-${creative.creative_id}`}>
          <span style={S.topPickStar}>★</span>
          Top pick · Score {score}
        </div>
      )}
      <div style={S.cardImg}>
        {isReady ? (
          <img src={creative.url} alt={creative.headline || "Ad"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : isError ? (
          <div style={S.pulseWrap}>
            <div style={{ ...S.pulseLabel, color: "#dc2626" }}>Failed — will retry</div>
          </div>
        ) : (
          <div style={S.pulseWrap}>
            <div style={S.pulse} />
            <div style={S.pulseLabel}>
              {isGenerating ? "Generating…" : "Waiting for kickoff"}
            </div>
          </div>
        )}
        {isReady && creative.format && (
          <div style={S.formatBadge}>{creative.format.replace(/_/g, " ")}</div>
        )}
        {isReady && score !== null && (
          <div
            style={{ ...S.shareScoreBadge, background: scoreColor, borderColor: scoreColor }}
            data-testid={`share-score-${creative.creative_id}`}
          >
            <span style={{ fontWeight: 800 }}>{score}</span>
            <span style={{ fontSize: 9, opacity: 0.9 }}>{scoreLabel}</span>
          </div>
        )}
        {isReady && (
          <div style={S.openHint} data-testid={`open-hint-${creative.creative_id}`}>
            Tap for details →
          </div>
        )}
        {/* Client reactions — ❤ love / ✕ pass. One-tap; persists via API. */}
        {isReady && (
          <div style={S.reactBar} data-testid={`react-bar-${creative.creative_id}`}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onReact?.(creative.client_reaction === "love" ? null : "love"); }}
              style={{
                ...S.reactBtn,
                ...(creative.client_reaction === "love" ? S.reactBtnActiveLove : {}),
              }}
              data-testid={`react-love-${creative.creative_id}`}
              aria-label={creative.client_reaction === "love" ? "Remove love" : "Love this creative"}
              title={creative.client_reaction === "love" ? "Tap to remove" : "Love it"}
            >
              <span style={S.reactIcon}>{creative.client_reaction === "love" ? "❤" : "♡"}</span>
              <span style={S.reactLabel}>{creative.client_reaction === "love" ? "Loved" : "Love"}</span>
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onReact?.(creative.client_reaction === "pass" ? null : "pass"); }}
              style={{
                ...S.reactBtn,
                ...(creative.client_reaction === "pass" ? S.reactBtnActivePass : {}),
              }}
              data-testid={`react-pass-${creative.creative_id}`}
              aria-label={creative.client_reaction === "pass" ? "Undo pass" : "Pass on this creative"}
              title={creative.client_reaction === "pass" ? "Tap to undo" : "Not a fit"}
            >
              <span style={S.reactIcon}>✕</span>
              <span style={S.reactLabel}>{creative.client_reaction === "pass" ? "Passed" : "Pass"}</span>
            </button>
          </div>
        )}
      </div>
      {/* Clean Meta stack — vertical HEADLINE / PRIMARY TEXT / DESCRIPTION,
          each with its own Copy button. This is the ad copy that gets pasted
          into Meta Ads Manager. No on-image copy duplication. */}
      {isReady && creative.meta_copy && (
        <MetaCardStack
          meta={creative.meta_copy}
          creativeId={creative.creative_id}
        />
      )}
      {isReady && (percentile !== null || cplLabel || strengths.length > 0) && (
        <div style={S.cardIntel} data-testid={`share-intel-${creative.creative_id}`}>
          {percentile !== null && (
            <div style={S.percentileLine}>
              <span style={S.percentileStar}>★</span>
              <span>
                Above the <strong>{percentile}th percentile</strong> for{" "}
                <strong>{trade.toLowerCase()}</strong> creatives in the last 90 days.
              </span>
            </div>
          )}
          {cplLabel && (
            <div style={S.cplRow}>
              <span style={S.cplLabel}>Predicted cost per lead</span>
              <span style={S.cplValue}>{cplLabel}</span>
            </div>
          )}
          {strengths.length > 0 && (
            <ul style={S.strengthList}>
              {strengths.map((s, i) => (
                <li key={i} style={S.strengthItem}>
                  <span style={S.check}>✓</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ShareCreativeModal({ creative, industry, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  const score = typeof creative.score === "number" ? creative.score : null;
  const scoreLabel = creative.score_label || (score >= 85 ? "Excellent" : score >= 70 ? "Strong" : "");
  const scoreColor = score >= 85 ? "#16a34a" : score >= 70 ? "#059669" : "#dc328c";
  const cpl = creative.predicted_cpl || {};
  const strengths = Array.isArray(creative.strengths) ? creative.strengths.slice(0, 4) : [];
  const percentile = score !== null ? Math.max(50, Math.min(99, score - 6)) : null;
  const trade = (industry || "").replace(/^Home Services$/i, "home service") || "home service";
  const monthLabel = (() => {
    const now = new Date();
    return `${now.toLocaleString("en-US", { month: "short" })} ${now.getFullYear()}`;
  })();

  // CPL gradient — position the marker for the "likely" midpoint
  const bestCase = cpl.best_case ?? cpl.likely_low;
  const riskCase = cpl.risk_case ?? cpl.likely_high;
  const likelyMid = cpl.likely ?? (cpl.likely_low && cpl.likely_high ? Math.round((cpl.likely_low + cpl.likely_high) / 2) : null);
  // Marker percent: 0% = best case, 100% = risk case
  const markerPct = (() => {
    if (bestCase == null || riskCase == null || likelyMid == null || riskCase <= bestCase) return 50;
    return Math.max(4, Math.min(96, Math.round(((likelyMid - bestCase) / (riskCase - bestCase)) * 100)));
  })();

  const cplRangeText = (cpl.likely_low && cpl.likely_high)
    ? `$${cpl.likely_low}–$${cpl.likely_high}`
    : (likelyMid ? `$${likelyMid}` : "—");

  // score ring — SVG stroke-dashoffset (0-100)
  const ringSize = 76;
  const ringStroke = 8;
  const ringR = (ringSize - ringStroke) / 2;
  const ringC = 2 * Math.PI * ringR;
  const ringOffset = score !== null ? ringC * (1 - Math.min(100, Math.max(0, score)) / 100) : ringC;

  return (
    <div style={M.overlay} onClick={onClose} data-testid="share-creative-modal">
      <div style={M.panel} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="share-modal-close" style={M.close} aria-label="close">✕</button>
        <div style={M.body}>
          {/* LEFT — big creative */}
          <div style={M.left}>
            <div style={M.imgFrame}>
              {creative.url && (
                <img
                  src={creative.url}
                  alt={creative.headline || "Ad"}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              )}
              {creative.format && (
                <div style={M.formatBadge}>{creative.format.replace(/_/g, " ")}</div>
              )}
            </div>
            {/* Copy panel */}
            <div style={M.copyPanel} data-testid="share-modal-copy">
              <div style={M.copyEyebrow}>The copy</div>
              {creative.headline && (
                <div style={M.copyHeadline}>{creative.headline}</div>
              )}
              {creative.subhead && (
                <div style={M.copySub}>{creative.subhead}</div>
              )}
              {creative.cta && (
                <div style={M.copyCtaCard}>
                  <div style={M.copyCtaLabel}>Call to action</div>
                  <div style={M.copyCtaText}>{creative.cta}</div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — CPL + score + strengths */}
          <div style={M.right}>
            {(bestCase != null || likelyMid != null || riskCase != null) && (
              <div style={M.cplCard} data-testid="share-modal-cpl">
                <div style={M.cplHead}>
                  <span style={M.cplPill}>$</span>
                  <span style={M.cplTitle}>Predicted CPL</span>
                  <span style={M.cplInfo} title="Predicted based on hook, offer, creative and industry benchmarks">ⓘ</span>
                  <span style={M.cplMonth}>{monthLabel}</span>
                </div>
                <div style={M.cplRangeBig}>{cplRangeText}<span style={M.cplRangeUnit}> likely cost per lead</span></div>
                <div style={M.cplIndustry}>{industry || "Home services"}</div>

                <div style={M.cplBarWrap}>
                  <div style={M.cplBar} />
                  <div
                    style={{ ...M.cplMarker, left: `${markerPct}%` }}
                    data-testid="share-modal-cpl-marker"
                  />
                </div>
                <div style={M.cplTicks}>
                  <div style={M.cplTickCol}>
                    <div style={{ ...M.cplTickValue, color: "#16a34a" }}>${bestCase ?? "—"}</div>
                    <div style={M.cplTickLabel}>Best case</div>
                  </div>
                  <div style={{ ...M.cplTickCol, textAlign: "center" }}>
                    <div style={{ ...M.cplTickValue, color: "#0a0a0a" }}>{cplRangeText}</div>
                    <div style={M.cplTickLabel}>Likely range</div>
                  </div>
                  <div style={{ ...M.cplTickCol, textAlign: "right" }}>
                    <div style={{ ...M.cplTickValue, color: "#dc2626" }}>${riskCase ?? "—"}</div>
                    <div style={M.cplTickLabel}>Risk case</div>
                  </div>
                </div>
              </div>
            )}

            {score !== null && (
              <div style={M.scoreCard} data-testid="share-modal-score">
                <div style={M.scoreRingWrap}>
                  <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)" }}>
                    <circle cx={ringSize / 2} cy={ringSize / 2} r={ringR}
                            stroke="#EAEAE0" strokeWidth={ringStroke} fill="none" />
                    <circle cx={ringSize / 2} cy={ringSize / 2} r={ringR}
                            stroke={scoreColor} strokeWidth={ringStroke} fill="none"
                            strokeDasharray={ringC} strokeDashoffset={ringOffset}
                            strokeLinecap="round"
                            style={{ transition: "stroke-dashoffset 0.6s ease" }} />
                  </svg>
                  <div style={{ ...M.scoreRingNum, color: scoreColor }}>{score}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={M.scoreTitle}>
                    Ad score <span style={{ color: scoreColor, fontWeight: 800 }}>{scoreLabel}</span>
                  </div>
                  {percentile !== null && (
                    <div style={M.scoreSub}>
                      Above the <strong>{percentile}th percentile</strong> for {trade.toLowerCase()} creatives in the last 90 days.
                    </div>
                  )}
                </div>
              </div>
            )}

            {strengths.length > 0 && (
              <div style={M.strengths} data-testid="share-modal-strengths">
                <div style={M.strengthLabel}>Why it works</div>
                <ul style={M.strengthList}>
                  {strengths.map((s, i) => (
                    <li key={i} style={M.strengthItem}>
                      <span style={M.strengthCheck}>✓</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* FULL WIDTH — Facebook Ads Manager copy (variants) */}
        {creative.meta_copy && (
          <MetaCopyBlock meta={creative.meta_copy} light />
        )}
      </div>
    </div>
  );
}

function MetaCopyBlock({ meta, light }) {
  const [copiedKey, setCopiedKey] = useState(null);
  const doCopy = async (key, text) => {
    if (!text) return;
    try { await navigator.clipboard.writeText(text); } catch { /* no-op */ }
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1400);
  };
  // Build variants list — if `variations` present use those, otherwise a single primary/headline/desc.
  let variants = Array.isArray(meta.variations) ? meta.variations.filter(v => v && (v.primary_text || v.headline)) : [];
  if (!variants.length && (meta.primary_text || meta.headline || meta.description)) {
    variants = [{ primary_text: meta.primary_text, headline: meta.headline, description: meta.description }];
  }
  if (!variants.length) return null;
  const styles = light ? META_L : META_D;
  const cta = meta.cta_type ? String(meta.cta_type).replace(/_/g, " ") : "LEARN MORE";
  return (
    <div style={styles.wrap} data-testid="meta-copy-block">
      <div style={styles.head}>
        <div style={styles.iconWrap}><MetaLogo size={22} /></div>
        <div style={{ flex: 1 }}>
          <div style={styles.eyebrow}>Meta ads copy · all variants</div>
          <div style={styles.title}>
            Paste-ready · {variants.length} variant{variants.length > 1 ? "s" : ""} · CTA <strong style={styles.ctaBadge}>{cta}</strong>
          </div>
        </div>
        {meta.link_url && (
          <a href={meta.link_url} target="_blank" rel="noopener noreferrer" style={styles.linkPill} data-testid="meta-copy-link">
            {new URL(meta.link_url.startsWith("http") ? meta.link_url : `https://${meta.link_url}`).host} →
          </a>
        )}
      </div>

      <div style={styles.variantsGrid}>
        {variants.map((v, i) => (
          <div key={i} style={styles.variantCard} data-testid={`meta-variant-${i}`}>
            <div style={styles.variantIdx}>Variant {String(i + 1).padStart(2, "0")}</div>
            {v.primary_text && (
              <MetaField label="Primary text" value={v.primary_text} styles={styles} tid={`primary-${i}`}
                         copied={copiedKey === `p${i}`} onCopy={() => doCopy(`p${i}`, v.primary_text)} multi />
            )}
            {v.headline && (
              <MetaField label="Headline" value={v.headline} styles={styles} tid={`headline-${i}`}
                         copied={copiedKey === `h${i}`} onCopy={() => doCopy(`h${i}`, v.headline)} />
            )}
            {v.description && (
              <MetaField label="Description" value={v.description} styles={styles} tid={`desc-${i}`}
                         copied={copiedKey === `d${i}`} onCopy={() => doCopy(`d${i}`, v.description)} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MetaField({ label, value, styles, copied, onCopy, tid, multi }) {
  return (
    <div style={styles.field} data-testid={`meta-field-${tid}`}>
      <div style={styles.fieldHead}>
        <span style={styles.fieldLabel}>{label}</span>
        <button onClick={onCopy} style={styles.copyBtn} data-testid={`meta-copy-btn-${tid}`}>
          {copied ? "✓ Copied" : "Copy"}
        </button>
      </div>
      <div style={{ ...styles.fieldBody, ...(multi ? styles.fieldBodyMulti : {}) }}>{value}</div>
    </div>
  );
}

function VideoTile({ video }) {
  const src = video.embed_url || video.url;
  const isEmbed = ["youtube", "vimeo", "loom", "drive"].includes(video.source);
  return (
    <div style={S.videoCard} data-testid={`share-video-${video.video_id}`}>
      <div style={S.videoPlayer}>
        {isEmbed ? (
          <iframe src={src} title={video.headline || "Video"} allow="autoplay; encrypted-media" allowFullScreen style={S.videoIframe} />
        ) : (
          <video src={src} controls preload="metadata" style={S.videoEl} />
        )}
      </div>
      {video.headline && <div style={S.videoHead}>{video.headline}</div>}
    </div>
  );
}

function MetaChip({ label, value }) {
  return (
    <div style={S.metaChip}>
      <div style={S.metaLabel}>{label}</div>
      <div style={S.metaValue}>{value}</div>
    </div>
  );
}

function StatusChip({ status, genStatus }) {
  const label = status === "final_ready" ? "Finals ready"
    : status === "call_attached" ? "Kickoff done"
    : status === "generating" ? "Drafting…"
    : status === "draft_ready" ? "Drafts ready"
    : "New";
  const isActive = genStatus === "generating";
  return (
    <div style={{ ...S.metaChip, borderColor: isActive ? "#dc328c" : "#DADAD0" }}>
      <div style={S.metaLabel}>Status</div>
      <div style={{ ...S.metaValue, color: isActive ? "#dc328c" : "#0a0a0a" }}>
        {isActive && <span style={S.tinySpin} />} {label}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div style={{ padding: "80px 0" }}>
      <div style={{ width: 220, height: 12, background: "#EAEAE0", borderRadius: 4, marginBottom: 24 }} />
      <div style={{ width: "80%", maxWidth: 620, height: 42, background: "#EAEAE0", borderRadius: 6, marginBottom: 12 }} />
      <div style={{ width: "60%", maxWidth: 520, height: 42, background: "#EAEAE0", borderRadius: 6, marginBottom: 40 }} />
      <div style={S.grid}>
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={{ ...S.card, background: "#F5F5F0" }}>
            <div style={S.cardImg}><div style={S.pulseWrap}><div style={S.pulse} /></div></div>
            <div style={{ height: 40 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function NotFound({ msg }) {
  return (
    <div style={{ padding: "120px 0 40px", textAlign: "center" }}>
      <div style={S.eyebrow}>404</div>
      <h1 style={S.h1}>{msg}</h1>
      <p style={{ ...S.lead, textAlign: "center", margin: "0 auto" }}>
        This share link may have expired or been rotated. Reach out and we'll get you a fresh one.
      </p>
      <div style={{ marginTop: 24 }}>
        <a href="mailto:info@skyeai.io" style={S.link}>info@skyeai.io</a>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
const S = {
  page: {
    minHeight: "100vh",
    background: "#FAFAF7",
    color: "#0a0a0a",
    fontFamily: "'Poppins', system-ui, -apple-system, sans-serif",
  },
  topBar: {
    position: "sticky", top: 0, zIndex: 20,
    background: "rgba(250,250,247,0.92)",
    backdropFilter: "blur(12px)",
    borderBottom: "1px solid #EAEAE0",
  },
  topInner: {
    maxWidth: 1080, margin: "0 auto",
    padding: "16px 32px",
    display: "flex", alignItems: "center", gap: 10,
  },
  brandTx: { fontWeight: 800, fontSize: 14, letterSpacing: "-0.01em" },
  privatePill: {
    fontSize: 10.5, fontFamily: "'Poppins', sans-serif",
    letterSpacing: 1.4, color: "#dc328c", textTransform: "uppercase",
    fontWeight: 700, padding: "5px 10px",
    border: "1px solid rgba(220,50,140,0.28)",
    borderRadius: 999, background: "rgba(220,50,140,0.05)",
  },
  content: { maxWidth: 1080, margin: "0 auto", padding: "0 32px 80px" },
  hero: { paddingTop: 60, paddingBottom: 32 },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
    color: "#dc328c", fontWeight: 700, marginBottom: 20,
  },
  eyebrowSmall: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10.5, letterSpacing: 1.6, textTransform: "uppercase",
    color: "#6b6b66", fontWeight: 700,
  },
  h1: {
    fontSize: "clamp(38px, 5vw, 56px)",
    fontWeight: 900, lineHeight: 1.05, letterSpacing: "-0.03em",
    marginBottom: 20,
    fontFamily: "'Poppins', sans-serif",
  },
  h1Accent: {
    color: "#dc328c", fontStyle: "italic", fontWeight: 900,
    fontFamily: "'Poppins', sans-serif",
  },
  lead: { fontSize: 17, lineHeight: 1.6, color: "#4a4a45", maxWidth: 620 },
  metaRow: { display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 },
  metaChip: {
    padding: "10px 14px",
    border: "1px solid #DADAD0",
    borderRadius: 8, background: "#fff",
    minWidth: 130,
  },
  metaLabel: {
    fontSize: 10, letterSpacing: 1.5, color: "#6b6b66",
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 700, textTransform: "uppercase", marginBottom: 4,
  },
  metaValue: {
    fontSize: 13.5, fontWeight: 700, color: "#0a0a0a",
    display: "flex", alignItems: "center", gap: 8,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 20, marginTop: 24,
  },
  roundTabsWrap: {
    display: "flex", flexDirection: "column", gap: 10,
    marginBottom: 4,
  },
  roundTabs: {
    display: "inline-flex", gap: 6,
    padding: 5,
    background: "#F5F5F0",
    border: "1px solid #EAEAE0",
    borderRadius: 999,
    alignSelf: "flex-start",
  },
  roundTab: {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "9px 18px",
    background: "transparent",
    border: "none",
    borderRadius: 999,
    fontFamily: "'Poppins', sans-serif",
    fontSize: 12, fontWeight: 700,
    letterSpacing: 0.6, textTransform: "uppercase",
    color: "#6b6b66",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  roundTabActive: {
    background: "#0a0a0a",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
  },
  roundTabCount: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: 20, height: 20, padding: "0 6px",
    background: "rgba(0,0,0,0.08)",
    color: "#6b6b66",
    borderRadius: 999,
    fontSize: 10.5, fontWeight: 800,
    letterSpacing: 0,
  },
  roundTabCountActive: {
    background: "#dc328c",
    color: "#fff",
  },
  roundSub: {
    display: "flex", alignItems: "center", gap: 6,
    fontFamily: "'Poppins', sans-serif",
    fontSize: 12.5,
    color: "#6b6b66",
    paddingLeft: 4,
  },
  roundArc: { fontWeight: 600, color: "#0a0a0a" },
  roundArcNote: { color: "#dc328c", fontWeight: 600 },
  card: {
    background: "#fff",
    border: "1px solid #EAEAE0",
    borderRadius: 12,
    overflow: "hidden",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    position: "relative",
  },
  cardTopPick: {
    border: "2px solid #dc328c",
    boxShadow: "0 8px 30px rgba(220,50,140,0.18)",
    transform: "translateY(-2px)",
  },
  topPickRibbon: {
    position: "absolute", top: 12, right: -6,
    padding: "6px 14px 6px 12px",
    background: "linear-gradient(135deg, #dc328c, #b91c69)",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10.5, fontWeight: 800, letterSpacing: 0.6,
    textTransform: "uppercase",
    borderRadius: "6px 0 6px 6px",
    boxShadow: "0 4px 14px rgba(220,50,140,0.35)",
    zIndex: 3,
    display: "flex", alignItems: "center", gap: 5,
  },
  topPickStar: {
    fontSize: 12, color: "#FFD700",
    textShadow: "0 0 4px rgba(255,215,0,0.5)",
  },
  cardImg: {
    aspectRatio: "1 / 1",
    background: "linear-gradient(135deg, #F5F5F0 0%, #EAEAE0 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative", overflow: "hidden",
  },
  pulseWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 14 },
  pulse: {
    width: 42, height: 42, borderRadius: "50%",
    background: "linear-gradient(135deg, #dc328c, #F472B6)",
    animation: "pulse 1.8s ease-in-out infinite",
    boxShadow: "0 0 0 0 rgba(220,50,140,0.4)",
  },
  pulseLabel: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10.5,
    letterSpacing: 1.4, textTransform: "uppercase",
    color: "#6b6b66", fontWeight: 700,
  },
  cardMeta: {
    padding: "12px 14px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    borderTop: "1px solid #EAEAE0",
  },
  cardSlot: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10.5,
    letterSpacing: 1.6, color: "#6b6b66",
    fontWeight: 700, textTransform: "uppercase",
  },
  cardHead: {
    fontSize: 12.5, fontWeight: 700, color: "#0a0a0a",
    lineHeight: 1.3, marginTop: 2,
  },
  cardStatus: { fontSize: 12, fontWeight: 700, color: "#0a0a0a", marginLeft: 8, flexShrink: 0 },
  formatBadge: {
    position: "absolute", top: 10, right: 10,
    padding: "4px 9px", background: "rgba(0,0,0,0.55)", color: "#fff",
    borderRadius: 999, fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2,
    textTransform: "uppercase", backdropFilter: "blur(6px)",
    fontFamily: "'Poppins', sans-serif",
  },
  shareScoreBadge: {
    position: "absolute", top: 10, left: 10,
    padding: "5px 10px", borderRadius: 999,
    border: "1px solid",
    color: "#fff",
    display: "flex", alignItems: "center", gap: 5,
    fontSize: 11.5, fontFamily: "'Poppins', sans-serif",
    boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
  },
  cardIntel: {
    padding: "14px 16px 16px",
    borderTop: "1px solid #EAEAE0",
    display: "flex", flexDirection: "column", gap: 12,
  },
  percentileLine: {
    display: "flex", gap: 8, alignItems: "flex-start",
    fontSize: 12.5, color: "#0a0a0a", lineHeight: 1.5,
  },
  percentileStar: {
    color: "#dc328c", fontSize: 14, lineHeight: 1.2, flexShrink: 0,
  },
  cplRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "10px 12px", background: "#FAFAF7",
    border: "1px solid #EAEAE0", borderRadius: 8,
  },
  cplLabel: {
    fontFamily: "'Poppins', sans-serif", fontSize: 10.5,
    letterSpacing: 1.4, textTransform: "uppercase",
    color: "#6b6b66", fontWeight: 700,
  },
  cplValue: {
    fontSize: 16, fontWeight: 800, color: "#059669",
    letterSpacing: "-0.01em",
  },
  strengthList: {
    listStyle: "none", padding: 0, margin: 0,
    display: "flex", flexDirection: "column", gap: 6,
  },
  strengthItem: {
    display: "flex", gap: 8, alignItems: "flex-start",
    fontSize: 12.5, color: "#3a3a35", lineHeight: 1.5,
  },
  check: {
    color: "#16a34a", fontWeight: 800, flexShrink: 0,
    fontSize: 13,
  },
  briefCard: {
    marginTop: 32, padding: "26px 30px",
    background: "#fff", border: "1px solid #DADAD0",
    borderRadius: 14,
    boxShadow: "0 3px 20px rgba(220,50,140,0.06)",
  },
  offer: {
    fontSize: 22, fontWeight: 800, color: "#0a0a0a", lineHeight: 1.3,
    letterSpacing: "-0.02em", marginTop: 8, marginBottom: 18,
  },
  hooks: { display: "flex", flexDirection: "column", gap: 12 },
  hookRow: {
    display: "flex", gap: 14, padding: "12px 14px",
    background: "#FAFAF7", borderRadius: 8, border: "1px solid #EAEAE0",
  },
  hookNum: {
    fontFamily: "'Poppins', sans-serif", fontSize: 11,
    letterSpacing: 1.4, color: "#dc328c", fontWeight: 700,
    minWidth: 24,
  },
  hookText: { fontSize: 13.5, color: "#0a0a0a", lineHeight: 1.55, fontWeight: 500 },
  h2: {
    fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 800, color: "#0a0a0a",
    letterSpacing: "-0.02em", margin: "6px 0 24px",
  },
  videoSection: { marginTop: 48 },
  videoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
    gap: 20, marginTop: 20,
  },
  videoCard: {
    background: "#fff", border: "1px solid #EAEAE0",
    borderRadius: 12, overflow: "hidden",
  },
  videoPlayer: {
    aspectRatio: "16 / 9", background: "#000",
  },
  videoIframe: { width: "100%", height: "100%", border: "none" },
  videoEl: { width: "100%", height: "100%", objectFit: "cover" },
  videoHead: {
    padding: "12px 14px", fontSize: 13.5, fontWeight: 700, color: "#0a0a0a",
    borderTop: "1px solid #EAEAE0",
  },
  footer: {
    marginTop: 60, padding: "36px 0", borderTop: "1px solid #EAEAE0",
    display: "grid", gridTemplateColumns: "1fr auto", gap: 24, alignItems: "start",
  },
  footerCol: { maxWidth: 460 },
  stepNum: {
    fontFamily: "'Poppins', sans-serif", fontSize: 11, letterSpacing: 1.4,
    color: "#dc328c", fontWeight: 700, paddingTop: 3, minWidth: 24,
  },
  footerContact: {
    padding: "12px 18px", background: "#F5F5F0", borderRadius: 10,
    fontFamily: "'Poppins', sans-serif", fontSize: 12.5,
    color: "#4a4a45", letterSpacing: 0.3,
  },
  link: { color: "#dc328c", fontWeight: 700, textDecoration: "none" },
  tinySpin: {
    width: 10, height: 10, borderRadius: "50%",
    border: "2px solid #dc328c", borderTopColor: "transparent",
    display: "inline-block", animation: "share-spin 0.9s linear infinite",
  },
  cardCopy: {
    padding: "10px 16px 14px",
    borderTop: "1px solid #EAEAE0",
    display: "flex", flexDirection: "column", gap: 8,
  },
  cardSub: {
    fontSize: 12.5, color: "#3a3a35", lineHeight: 1.5,
    fontStyle: "italic",
  },
  cardCta: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "8px 10px",
    background: "#FAFAF7",
    border: "1px solid #EAEAE0",
    borderRadius: 6,
  },
  cardCtaLabel: {
    fontFamily: "'Poppins', sans-serif", fontSize: 9.5,
    letterSpacing: 1.4, textTransform: "uppercase",
    color: "#6b6b66", fontWeight: 800, flexShrink: 0,
  },
  cardCtaText: {
    fontSize: 12, color: "#0a0a0a", fontWeight: 600,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  cardMetaPreview: {
    padding: "12px 16px 14px",
    borderTop: "1px solid #EAEAE0",
    background: "#F5FAFF",
    display: "flex", flexDirection: "column", gap: 8,
    fontFamily: "'Poppins', sans-serif",
  },
  cardMetaHead: { display: "flex", alignItems: "center", gap: 6 },
  cardMetaIcon: {
    width: 16, height: 16, borderRadius: "50%",
    background: "#1877F2", color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 10, fontWeight: 900,
    fontFamily: "Georgia, serif", fontStyle: "italic",
  },
  cardMetaLabel: {
    fontSize: 9.5, letterSpacing: 1.4, textTransform: "uppercase",
    fontWeight: 800, color: "#1E40AF", flex: 1,
  },
  cardMetaMore: {
    fontSize: 9.5, fontWeight: 700, color: "#1877F2",
    padding: "2px 7px", borderRadius: 999,
    background: "rgba(24,119,242,0.12)",
  },
  cardMetaPrimary: {
    fontSize: 12, color: "#0a0a0a", lineHeight: 1.5,
    display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardMetaSubGrid: { display: "flex", flexDirection: "column", gap: 4 },
  cardMetaSub: {
    display: "flex", alignItems: "baseline", gap: 6,
    fontSize: 11, lineHeight: 1.4,
  },
  cardMetaSubLabel: {
    fontSize: 8.5, letterSpacing: 1.2, textTransform: "uppercase",
    color: "#6b6b66", fontWeight: 800, flexShrink: 0, minWidth: 62,
  },
  cardMetaSubText: {
    color: "#0a0a0a", fontWeight: 600,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  // ── Meta ads copy — clean vertical stack shown ON THE CARD ─────
  metaStack: {
    padding: "16px 18px 18px",
    borderTop: "1px solid #EAEAE0",
    background: "#fff",
    display: "flex", flexDirection: "column", gap: 12,
    fontFamily: "'Poppins', sans-serif",
  },
  metaStackHead: {
    display: "flex", alignItems: "center", gap: 8,
    paddingBottom: 6,
    borderBottom: "1px solid #F0F0EA",
  },
  metaStackBrand: {
    fontSize: 12, letterSpacing: 0.6, textTransform: "uppercase",
    color: "#0a0a0a", fontWeight: 800, flex: 1,
  },
  metaStackMore: {
    fontSize: 10, letterSpacing: 0.4, textTransform: "uppercase",
    color: "#6b6b66", fontWeight: 700,
    padding: "3px 8px", borderRadius: 999,
    background: "#F5F5F0", border: "1px solid #EAEAE0",
  },
  metaStackField: { display: "flex", flexDirection: "column", gap: 4 },
  metaStackFieldHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  metaStackFieldLabel: {
    fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase",
    color: "#6b6b66", fontWeight: 800,
  },
  metaStackCopy: {
    display: "inline-flex", alignItems: "center",
    padding: "4px 10px", borderRadius: 999,
    background: "#fff", border: "1px solid #DADAD0",
    color: "#0a0a0a", fontWeight: 700, fontSize: 11,
    fontFamily: "'Poppins', sans-serif",
    cursor: "pointer", letterSpacing: 0.2,
    transition: "background 0.15s ease, color 0.15s ease, border-color 0.15s ease",
  },
  metaStackCopyActive: {
    background: "#16a34a", borderColor: "#16a34a", color: "#fff",
  },
  metaStackFieldValue: {
    fontSize: 13.5, lineHeight: 1.55,
    padding: "10px 12px", borderRadius: 8,
    background: "#FAFAF7",
    border: "1px solid #EAEAE0",
    color: "#0a0a0a",
    whiteSpace: "pre-wrap", wordBreak: "break-word",
  },
  metaStackFieldValueMulti: { minHeight: 76 },
  // ── Client reactions overlay on the image ─────
  reactBar: {
    position: "absolute", left: 10, bottom: 10, zIndex: 3,
    display: "flex", gap: 6,
  },
  reactBtn: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "6px 11px", borderRadius: 999,
    background: "rgba(255,255,255,0.94)",
    color: "#0a0a0a",
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.14)",
    cursor: "pointer",
    fontFamily: "'Poppins', sans-serif",
    fontSize: 11.5, fontWeight: 700, letterSpacing: 0.2,
    transition: "transform 0.15s ease, background 0.15s ease, color 0.15s ease",
  },
  reactBtnActiveLove: {
    background: "#dc328c", color: "#fff",
    borderColor: "#dc328c",
    boxShadow: "0 6px 20px rgba(220,50,140,0.35)",
  },
  reactBtnActivePass: {
    background: "#0a0a0a", color: "#fff",
    borderColor: "#0a0a0a",
    boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
  },
  reactIcon: { fontSize: 13, lineHeight: 1 },
  reactLabel: { fontSize: 11 },
  openHint: {
    position: "absolute", bottom: 10, right: 10,
    padding: "5px 10px", borderRadius: 999,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
    textTransform: "uppercase",
    backdropFilter: "blur(6px)",
    opacity: 0,
    transition: "opacity 0.2s ease",
    pointerEvents: "none",
  },
};

// ── Detail modal styles (light theme, mockup-matching CPL gradient) ─────
const M = {
  overlay: {
    position: "fixed", inset: 0, zIndex: 60,
    background: "rgba(10,10,10,0.55)",
    backdropFilter: "blur(8px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 24,
    animation: "share-modal-fade 0.2s ease",
  },
  panel: {
    position: "relative",
    background: "#fff",
    borderRadius: 20,
    boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
    width: "min(1080px, 100%)",
    maxHeight: "90vh",
    overflow: "auto",
    fontFamily: "'Poppins', sans-serif",
  },
  close: {
    position: "absolute", top: 14, right: 14, zIndex: 2,
    width: 36, height: 36, borderRadius: "50%",
    background: "#F5F5F0", border: "1px solid #EAEAE0",
    color: "#0a0a0a", fontSize: 15, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.15s ease",
  },
  body: {
    display: "grid",
    gridTemplateColumns: "1.1fr 1fr",
    gap: 28,
    padding: 32,
  },
  left: { display: "flex", flexDirection: "column", gap: 20 },
  right: { display: "flex", flexDirection: "column", gap: 16 },
  imgFrame: {
    position: "relative",
    aspectRatio: "1 / 1",
    background: "#F5F5F0",
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid #EAEAE0",
  },
  formatBadge: {
    position: "absolute", top: 12, left: 12,
    padding: "5px 10px", borderRadius: 999,
    background: "rgba(0,0,0,0.62)", color: "#fff",
    fontSize: 10, fontWeight: 700, letterSpacing: 1.2,
    textTransform: "uppercase", backdropFilter: "blur(6px)",
  },
  copyPanel: {
    background: "#FAFAF7",
    border: "1px solid #EAEAE0",
    borderRadius: 12,
    padding: 18,
    display: "flex", flexDirection: "column", gap: 10,
  },
  copyEyebrow: {
    fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase",
    color: "#6b6b66", fontWeight: 800,
  },
  copyHeadline: {
    fontSize: 18, fontWeight: 800, color: "#0a0a0a",
    lineHeight: 1.25, letterSpacing: "-0.01em",
  },
  copySub: {
    fontSize: 13.5, color: "#3a3a35", lineHeight: 1.55,
  },
  copyCtaCard: {
    marginTop: 4, padding: "10px 12px",
    background: "#fff",
    border: "1px dashed #dc328c",
    borderRadius: 8,
    display: "flex", flexDirection: "column", gap: 3,
  },
  copyCtaLabel: {
    fontSize: 9.5, letterSpacing: 1.4, textTransform: "uppercase",
    color: "#dc328c", fontWeight: 800,
  },
  copyCtaText: { fontSize: 13.5, color: "#0a0a0a", fontWeight: 600 },
  cplCard: {
    background: "#F8F5FF",
    border: "1px solid #E7DDFF",
    borderRadius: 14,
    padding: 20,
  },
  cplHead: {
    display: "flex", alignItems: "center", gap: 6,
    marginBottom: 12,
  },
  cplPill: {
    width: 20, height: 20, borderRadius: "50%",
    background: "#5B21B6",
    color: "#fff", fontSize: 11, fontWeight: 800,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  cplTitle: {
    fontSize: 10.5, letterSpacing: 1.6, textTransform: "uppercase",
    color: "#5B21B6", fontWeight: 800,
  },
  cplInfo: { color: "#8B7CB8", fontSize: 12.5, cursor: "help" },
  cplMonth: {
    marginLeft: "auto", fontSize: 11, color: "#6b6b66", fontWeight: 600,
  },
  cplRangeBig: {
    fontSize: 28, fontWeight: 800, color: "#0a0a0a",
    letterSpacing: "-0.02em", lineHeight: 1.15,
  },
  cplRangeUnit: { fontSize: 12.5, fontWeight: 500, color: "#6b6b66", marginLeft: 6 },
  cplIndustry: {
    marginTop: 3, fontSize: 12, color: "#6b6b66", fontWeight: 600,
    marginBottom: 20,
  },
  cplBarWrap: { position: "relative", marginTop: 4, marginBottom: 16 },
  cplBar: {
    height: 8, borderRadius: 999,
    background: "linear-gradient(90deg, #16a34a 0%, #FBBF24 50%, #ef4444 100%)",
  },
  cplMarker: {
    position: "absolute", top: -6,
    transform: "translateX(-50%)",
    width: 20, height: 20, borderRadius: "50%",
    background: "#fff",
    border: "3px solid #0a0a0a",
    boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
  },
  cplTicks: { display: "flex", justifyContent: "space-between", gap: 8 },
  cplTickCol: { flex: 1, textAlign: "left" },
  cplTickValue: { fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em" },
  cplTickLabel: {
    marginTop: 2, fontSize: 11, color: "#6b6b66", fontWeight: 600,
  },
  scoreCard: {
    display: "flex", alignItems: "center", gap: 16,
    padding: 18,
    background: "#fff",
    border: "1px solid #EAEAE0",
    borderRadius: 14,
  },
  scoreRingWrap: {
    position: "relative", width: 76, height: 76,
    flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  scoreRingNum: {
    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em",
  },
  scoreTitle: { fontSize: 14, fontWeight: 700, color: "#0a0a0a", marginBottom: 4 },
  scoreSub: { fontSize: 12.5, color: "#3a3a35", lineHeight: 1.5 },
  strengths: {
    padding: 18,
    background: "#F5FFF5",
    border: "1px solid #D1FAE5",
    borderRadius: 14,
  },
  strengthLabel: {
    fontSize: 10.5, letterSpacing: 1.6, textTransform: "uppercase",
    color: "#059669", fontWeight: 800, marginBottom: 10,
  },
  strengthList: { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 },
  strengthItem: {
    display: "flex", gap: 8, alignItems: "flex-start",
    fontSize: 13, color: "#0a0a0a", lineHeight: 1.5,
  },
  strengthCheck: { color: "#16a34a", fontWeight: 800, flexShrink: 0, fontSize: 14 },
};

// ── Meta Ads Manager copy — LIGHT (share page) & DARK (admin) ──────────
const META_BASE = {
  variantIdx: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase",
    fontWeight: 800, marginBottom: 8,
  },
  field: { display: "flex", flexDirection: "column", gap: 5, marginTop: 10 },
  fieldHead: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  fieldLabel: {
    fontSize: 9.5, letterSpacing: 1.4, textTransform: "uppercase",
    fontWeight: 800,
  },
  copyBtn: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
    padding: "4px 10px", borderRadius: 999,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  fieldBody: {
    fontSize: 13, lineHeight: 1.55, borderRadius: 8, padding: "10px 12px",
    fontFamily: "'Poppins', sans-serif",
    whiteSpace: "pre-wrap", wordBreak: "break-word",
  },
  fieldBodyMulti: { minHeight: 72 },
};

const META_L = {
  wrap: {
    margin: "24px 32px 32px",
    background: "#F5FAFF",
    border: "1px solid #DBEAFE",
    borderRadius: 16,
    padding: 20,
    fontFamily: "'Poppins', sans-serif",
  },
  head: {
    display: "flex", alignItems: "center", gap: 12,
    marginBottom: 14,
  },
  iconWrap: {
    width: 38, height: 38, borderRadius: "50%",
    background: "#1877F2",
    color: "#fff", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 20, fontWeight: 900,
    fontFamily: "Georgia, serif",
    fontStyle: "italic",
  },
  icon: { lineHeight: 1 },
  eyebrow: {
    fontSize: 10.5, letterSpacing: 1.6, textTransform: "uppercase",
    color: "#1E40AF", fontWeight: 800, marginBottom: 2,
  },
  title: { fontSize: 13, color: "#0a0a0a", fontWeight: 600 },
  ctaBadge: {
    display: "inline-block", padding: "1px 8px",
    background: "#1877F2", color: "#fff", borderRadius: 4,
    fontSize: 10.5, fontWeight: 800, letterSpacing: 0.8,
    marginLeft: 4,
  },
  linkPill: {
    padding: "5px 10px", borderRadius: 999,
    background: "#fff", border: "1px solid #DBEAFE",
    color: "#1E40AF", fontSize: 11, fontWeight: 700,
    textDecoration: "none", flexShrink: 0,
  },
  variantsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 14,
  },
  variantCard: {
    background: "#fff",
    border: "1px solid #DBEAFE",
    borderRadius: 12,
    padding: 16,
  },
  ...META_BASE,
  variantIdx: { ...META_BASE.variantIdx, color: "#1E40AF" },
  fieldLabel: { ...META_BASE.fieldLabel, color: "#6b6b66" },
  fieldBody: { ...META_BASE.fieldBody, background: "#FAFAF7", color: "#0a0a0a", border: "1px solid #EAEAE0" },
  copyBtn: {
    ...META_BASE.copyBtn,
    background: "#1877F2", color: "#fff", border: "1px solid #1877F2",
  },
};

const META_D = {
  wrap: {
    background: "rgba(24,119,242,0.06)",
    border: "1px solid rgba(24,119,242,0.28)",
    borderRadius: 14, padding: 18,
    marginTop: 6,
  },
  head: {
    display: "flex", alignItems: "center", gap: 12,
    marginBottom: 14,
  },
  iconWrap: {
    width: 34, height: 34, borderRadius: "50%",
    background: "#1877F2", color: "#fff", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 18, fontWeight: 900,
    fontFamily: "Georgia, serif", fontStyle: "italic",
  },
  icon: { lineHeight: 1 },
  eyebrow: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 10, letterSpacing: 1.6, textTransform: "uppercase",
    color: "#60A5FA", fontWeight: 800, marginBottom: 2,
  },
  title: { fontSize: 12.5, color: "rgba(255,255,255,0.85)", fontWeight: 600 },
  ctaBadge: {
    display: "inline-block", padding: "1px 8px",
    background: "#1877F2", color: "#fff", borderRadius: 4,
    fontSize: 10, fontWeight: 800, letterSpacing: 0.8,
    marginLeft: 4,
  },
  linkPill: {
    padding: "5px 10px", borderRadius: 999,
    background: "rgba(24,119,242,0.15)", border: "1px solid rgba(24,119,242,0.4)",
    color: "#93C5FD", fontSize: 10.5, fontWeight: 700,
    textDecoration: "none", flexShrink: 0,
  },
  variantsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 12,
  },
  variantCard: {
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 10, padding: 14,
  },
  ...META_BASE,
  variantIdx: { ...META_BASE.variantIdx, color: "#60A5FA" },
  fieldLabel: { ...META_BASE.fieldLabel, color: "rgba(255,255,255,0.5)" },
  fieldBody: {
    ...META_BASE.fieldBody,
    background: "rgba(0,0,0,0.3)", color: "rgba(255,255,255,0.92)",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  copyBtn: {
    ...META_BASE.copyBtn,
    background: "rgba(24,119,242,0.2)", color: "#93C5FD",
    border: "1px solid rgba(24,119,242,0.5)",
  },
};

// Inject keyframes once (avoids TS-flag-heavy CSS-in-JS libs).
if (typeof document !== "undefined" && !document.getElementById("share-page-anim")) {
  const st = document.createElement("style");
  st.id = "share-page-anim";
  st.textContent = `
    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 0.9; box-shadow: 0 0 0 0 rgba(220,50,140,0.4); }
      50% { transform: scale(1.08); opacity: 1; box-shadow: 0 0 0 18px rgba(220,50,140,0); }
    }
    @keyframes share-spin { to { transform: rotate(360deg); } }
    @keyframes share-modal-fade {
      from { opacity: 0; transform: scale(0.98); }
      to { opacity: 1; transform: scale(1); }
    }
    /* Card open-hint fades in on hover (desktop) */
    @media (hover: hover) {
      [data-testid^="share-creative-"]:hover [data-testid^="open-hint-"] {
        opacity: 1;
      }
      [data-testid^="share-creative-"] [data-testid^="react-bar-"] { opacity: 0.4; transition: opacity 0.2s ease; }
      [data-testid^="share-creative-"]:hover [data-testid^="react-bar-"] { opacity: 1; }
    }
    /* Mobile-first: reactions and open-hint stay visible on touch devices */
    @media (hover: none) {
      [data-testid^="open-hint-"] { opacity: 0.85; }
      [data-testid^="react-bar-"] { opacity: 1; }
    }
    /* ── SharePage responsive tuning (mobile is the primary surface) ── */
    @media (max-width: 720px) {
      /* Hero: tighter padding, smaller H1 */
      [data-testid="share-hero"] { padding: 16px 4px !important; }
      [data-testid="share-hero"] h1 {
        font-size: clamp(30px, 8vw, 42px) !important;
        letter-spacing: -0.02em !important;
      }
      /* Modal: stack columns, fill screen */
      [data-testid="share-creative-modal"] > div {
        max-height: 100vh !important;
        border-radius: 0 !important;
        width: 100% !important;
      }
      [data-testid="share-creative-modal"] > div > div:last-child {
        grid-template-columns: 1fr !important;
        padding: 20px !important;
        gap: 20px !important;
      }
      /* Meta-copy block variants: single column */
      [data-testid="meta-copy-block"] > div:last-child {
        grid-template-columns: 1fr !important;
      }
      /* Round tabs: allow wrap */
      [data-testid="round-tabs"] { flex-wrap: wrap; }
    }
    @media (max-width: 480px) {
      /* Reaction pill: label hides, icon stays — save horizontal space */
      [data-testid^="react-love-"] span:last-child,
      [data-testid^="react-pass-"] span:last-child { display: none; }
      [data-testid^="react-bar-"] { gap: 5px; left: 8px; bottom: 8px; }
      /* Format & score badges: shrink a touch */
      [data-testid^="share-score-"] { font-size: 10.5px !important; }
    }
  `;
  document.head.appendChild(st);
}
